import { useEffect, useMemo, useRef, useState } from "react";
import { Share2 } from "lucide-react";
import type { Card } from "../../../../packages/shared/src/types";
import { api, ApiError, secret } from "../lib/api";
import { draftStore } from "../lib/drafts";
import { t as msg } from "../lib/i18n";
import { ConfirmDialog } from "./ConfirmDialog";

export function CardManagement({
  card,
  accountId,
  updated,
}: {
  card: Card;
  accountId: string;
  updated: () => void;
}) {
  const store = useMemo(() => draftStore(accountId), [accountId]);
  const [url, setURL] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [copied, setCopied] = useState(false);
  const [action, setAction] = useState<"revoke" | "rotate" | "extend" | null>(
      null,
    ),
    [expiry, setExpiry] = useState("");
  const writing = useRef(false);
  const acknowledgedLink = useRef<{
    owner: string;
    card: string;
    url: string;
    tokenRev: number;
  } | null>(null);
  const canShare =
    card.deliveryMode === "link" &&
    !["expired", "declined", "redeemed"].includes(card.status) &&
    card.moderation !== "pending";
  useEffect(() => {
    let live = true;
    const acknowledged = acknowledgedLink.current;
    if (
      acknowledged?.owner === accountId &&
      acknowledged.card === card.id &&
      acknowledged.tokenRev === card.tokenRev
    ) {
      setURL(acknowledged.url);
      return;
    }
    setURL("");
    void (async () => {
      const saved = await store.link(card.id);
      if (saved?.url && saved.tokenRev === card.tokenRev) {
        if (live) setURL(saved.url);
        return;
      }
      if ((card.tokenRev || 1) === 1) {
        const draft = (await store.all()).find((d) => d.sentCardId === card.id);
        if (live && draft?.shareToken)
          setURL(`${location.origin}/c/${card.id}?k=${draft.shareToken}`);
      }
    })().catch((e) => {
      if (live) setError(e.message);
    });
    return () => {
      live = false;
    };
  }, [store, card.id, card.tokenRev]);
  async function perform() {
    if (writing.current || !action) return;
    writing.current = true;
    setBusy(true);
    setError("");
    try {
      if (action === "revoke") {
        await api(`/api/cards/${card.id}/revoke`, "POST", {
          expectedVersion: card.version,
        });
        location.assign("/cards?tab=sent");
        return;
      }
      if (action === "extend") {
        await api(`/api/cards/${card.id}/extend`, "POST", {
          expectedVersion: card.version,
          expiresAt: new Date(expiry).toISOString(),
        });
      }
      if (action === "rotate") {
        const pending = (await store.link(card.id))?.pending || {
          clientRequestId: crypto.randomUUID(),
          shareToken: secret(),
        };
        await store.saveLink(card.id, { pending });
        const result = await api<{ shareUrl: string; tokenRev: number }>(
          `/api/cards/${card.id}/reissue-link`,
          "POST",
          pending,
        );
        acknowledgedLink.current = {
          owner: accountId,
          card: card.id,
          ...result,
          url: result.shareUrl,
        };
        setURL(result.shareUrl);
        await store
          .saveLink(card.id, {
            url: result.shareUrl,
            tokenRev: result.tokenRev,
          })
          .catch(() =>
            setError(
              msg("Copy this link now. It could not be saved on this device."),
            ),
          );
      }
      setAction(null);
      updated();
    } catch (e) {
      if (e instanceof ApiError && e.code === "stale_link_version")
        await store.saveLink(card.id, {}).catch(() => {});
      setError((e as Error).message);
    } finally {
      writing.current = false;
      setBusy(false);
    }
  }
  async function share() {
    try {
      if (navigator.share) await navigator.share({ title: card.title, url });
      else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
      }
    } catch (e) {
      if ((e as Error).name !== "AbortError")
        setError(msg("Select and copy the link below."));
    }
  }
  return (
    <div className="card-management">
      {canShare && url && (
        <>
          <button
            className="button secondary full"
            onClick={() => void share()}
          >
            <Share2 size={17} />
            {msg(copied ? "Link copied" : "Share card")}
          </button>
          <details className="share-link-details">
            <summary>{msg("Copy link")}</summary>
            <input
              aria-label={msg("Card link")}
              readOnly
              value={url}
              onFocus={(e) => e.target.select()}
            />
          </details>
        </>
      )}
      {(card.canRevoke || canShare || card.status === "expired") && (
        <details className="card-disclosure">
          <summary>{msg("Manage card")}</summary>
          <div className="management-actions">
            {canShare && (
              <button
                className="text-button"
                disabled={busy}
                onClick={() => {
                  setError("");
                  setAction("rotate");
                }}
              >
                {msg("Create a new share link")}
              </button>
            )}
            {card.status === "expired" && (
              <button
                className="text-button"
                disabled={busy}
                onClick={() => {
                  setError("");
                  setAction("extend");
                }}
              >
                {msg("Extend expiry")}
              </button>
            )}
            {card.canRevoke && (
              <button
                className="text-button danger-text"
                disabled={busy}
                onClick={() => {
                  setError("");
                  setAction("revoke");
                }}
              >
                {msg("Revoke card")}
              </button>
            )}
          </div>
        </details>
      )}
      {action && (
        <ConfirmDialog
          title={msg(
            action === "revoke"
              ? "Take back this card?"
              : action === "rotate"
                ? "Create a new share link?"
                : "Extend expiry",
          )}
          confirmLabel={msg(
            action === "revoke"
              ? "Revoke card"
              : action === "rotate"
                ? "Create a new share link"
                : "Save",
          )}
          cancelLabel={msg("Cancel")}
          destructive={action === "revoke"}
          busy={busy}
          onCancel={() => setAction(null)}
          onConfirm={() => void perform()}
        >
          {action === "revoke" ? (
            msg(
              "This card will disappear from your cards. Its link will stop opening.",
            )
          ) : action === "rotate" ? (
            msg(
              "The old link will stop opening this card. Its current owner stays the same.",
            )
          ) : (
            <label>
              {msg("Use before")}
              <input
                type="datetime-local"
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
              />
            </label>
          )}
          {error && (
            <span className="error" role="alert">
              {error}
            </span>
          )}
        </ConfirmDialog>
      )}
      {error && !action && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
